import React, { useEffect, useState, useCallback } from 'react';
import {
    Box, Typography, FormControl, InputLabel, Select, MenuItem,
    Button, IconButton, Avatar, Chip, Divider, CircularProgress, Tooltip,
    Dialog, DialogTitle, DialogContent, DialogActions, TextField, InputAdornment,
    Snackbar, Alert, Skeleton,
} from '@mui/material';
import GroupIcon from '@mui/icons-material/Group';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import SwapHorizIcon from '@mui/icons-material/SwapHoriz';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import SearchIcon from '@mui/icons-material/Search';
import PersonSearchIcon from '@mui/icons-material/PersonSearch';
import { useDispatch, useSelector } from 'react-redux';
import axios from 'axios';
import Cookies from 'universal-cookie';
import { fetchBusinessesFromAPI } from '../../Redux/BusinessDucks';

const getHeaders = () => {
    const cookies = new Cookies();
    return { headers: { Authorization: `Bearer ${cookies.get('token')}` } };
};

// Líneas de negocio del usuario (cookie). Super-Admin: null (todas).
const getUserBusinessLineIds = () => {
    const cookies = document.cookie.split('; ');
    const perfilCookie = cookies.find(c => c.startsWith('perfil='));
    const perfil = perfilCookie ? decodeURIComponent(perfilCookie.split('=')[1]) : '';
    if (perfil === 'Super-Admin') return null;
    const blCookie = cookies.find(c => c.startsWith('businessLineIds='));
    if (!blCookie) return [];
    return blCookie.split('=')[1].split(',').map(Number).filter(Boolean);
};

const fmtPhone = (tel) => {
    if (!tel) return '';
    const s = String(tel);
    return s.length > 4 ? `+(${s.substring(0, 2)}) ${s.substring(2)}` : s;
};

const getInitials = (name) => {
    if (!name?.trim()) return '??';
    return name.trim().split(' ').slice(0, 2).map(w => w[0]?.toUpperCase()).join('');
};

const GestionCarterasPage = () => {
    const dispatch = useDispatch();
    const businessLines = useSelector(s => s.business) || [];
    const userBLIds = getUserBusinessLineIds();

    const [selectedBL, setSelectedBL] = useState('');
    const [agents, setAgents] = useState([]);
    const [selectedAgent, setSelectedAgent] = useState('');
    const [cartera, setCartera] = useState([]);
    const [loadingCartera, setLoadingCartera] = useState(false);
    const [feedback, setFeedback] = useState(null); // { type, msg }

    // Diálogo agregar cliente
    const [addOpen, setAddOpen] = useState(false);
    const [search, setSearch] = useState('');
    const [searchResults, setSearchResults] = useState([]);
    const [searching, setSearching] = useState(false);

    // Diálogo reasignar
    const [reassignRow, setReassignRow] = useState(null); // assignment a mover
    const [reassignTarget, setReassignTarget] = useState('');
    const [working, setWorking] = useState(false);

    // Opciones de línea de negocio visibles según permisos
    const blOptions = (userBLIds === null)
        ? businessLines
        : businessLines.filter(bl => userBLIds.includes(Number(bl.id)));

    useEffect(() => { dispatch(fetchBusinessesFromAPI()); }, [dispatch]);

    // Default BL cuando llegan las líneas
    useEffect(() => {
        if (!selectedBL && blOptions.length > 0) setSelectedBL(blOptions[0].id);
    }, [blOptions, selectedBL]);

    // Cargar consultores de la línea seleccionada
    useEffect(() => {
        setSelectedAgent('');
        setCartera([]);
        if (!selectedBL) { setAgents([]); return; }
        let cancel = false;
        axios.get(`${window.API_BASE_URL}/assignments/agents`, {
            ...getHeaders(),
            params: { businessLineId: selectedBL },
        })
            .then(r => { if (!cancel) setAgents(Array.isArray(r.data) ? r.data : []); })
            .catch(() => { if (!cancel) setAgents([]); });
        return () => { cancel = true; };
    }, [selectedBL]);

    // Cargar cartera del consultor seleccionado
    const loadCartera = useCallback(() => {
        if (!selectedAgent || !selectedBL) { setCartera([]); return; }
        setLoadingCartera(true);
        axios.get(`${window.API_BASE_URL}/assignments/agent/${selectedAgent}/cartera`, {
            ...getHeaders(),
            params: { businessLineId: selectedBL },
        })
            .then(r => setCartera(Array.isArray(r.data) ? r.data : []))
            .catch(e => {
                setCartera([]);
                if (e?.response?.status === 403) {
                    setFeedback({ type: 'error', msg: 'No tienes permisos de supervisor para gestionar carteras.' });
                }
            })
            .finally(() => setLoadingCartera(false));
    }, [selectedAgent, selectedBL]);

    useEffect(() => { loadCartera(); }, [loadCartera]);

    // Buscar clientes para agregar
    useEffect(() => {
        if (!addOpen || !search || search.length < 2) { setSearchResults([]); return; }
        const t = setTimeout(async () => {
            setSearching(true);
            try {
                const res = await axios.get(`${window.API_BASE_URL}/Clients`, {
                    ...getHeaders(),
                    params: { search, page: 1, pageSize: 10 },
                });
                const raw = res.data;
                setSearchResults(Array.isArray(raw) ? raw : (raw?.items || []));
            } catch { setSearchResults([]); }
            finally { setSearching(false); }
        }, 400);
        return () => clearTimeout(t);
    }, [search, addOpen]);

    const agentName = agents.find(a => a.userId === selectedAgent)?.name || '';

    // ── Acciones ────────────────────────────────────────────────────────────────
    const assign = async (clientId, agentUserId) => {
        await axios.post(`${window.API_BASE_URL}/assignments/manual`,
            { clientId, agentUserId, businessLineId: selectedBL }, getHeaders());
    };

    const handleAddClient = async (client) => {
        setWorking(true);
        try {
            await assign(client.id, selectedAgent);
            setFeedback({ type: 'success', msg: `${client.fullName || client.name || 'Cliente'} agregado a la cartera.` });
            setAddOpen(false); setSearch(''); setSearchResults([]);
            loadCartera();
        } catch (e) {
            setFeedback({ type: 'error', msg: e?.response?.data || 'No se pudo agregar el cliente.' });
        } finally { setWorking(false); }
    };

    const handleReassign = async () => {
        if (!reassignRow || !reassignTarget) return;
        setWorking(true);
        try {
            await assign(reassignRow.clientId, reassignTarget);
            const target = agents.find(a => a.userId === reassignTarget)?.name || 'otro consultor';
            setFeedback({ type: 'success', msg: `${reassignRow.clientName || 'Cliente'} reasignado a ${target}.` });
            setReassignRow(null); setReassignTarget('');
            loadCartera();
        } catch (e) {
            setFeedback({ type: 'error', msg: e?.response?.data || 'No se pudo reasignar.' });
        } finally { setWorking(false); }
    };

    const handleRemove = async (row) => {
        setWorking(true);
        try {
            await axios.delete(
                `${window.API_BASE_URL}/assignments/${row.clientId}/businessLine/${selectedBL}`,
                getHeaders());
            setFeedback({ type: 'success', msg: `${row.clientName || 'Cliente'} quitado de la cartera.` });
            loadCartera();
        } catch (e) {
            setFeedback({ type: 'error', msg: e?.response?.data || 'No se pudo quitar el cliente.' });
        } finally { setWorking(false); }
    };

    // ── Render ────────────────────────────────────────────────────────────────
    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', overflow: 'hidden', bgcolor: '#fff' }}>
            {/* Header */}
            <Box sx={{
                px: 2.5, py: 1.5, borderBottom: '1px solid #e5e7eb',
                display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap',
            }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <GroupIcon sx={{ color: '#7c3aed' }} />
                    <Typography variant="h6" fontWeight={700}>Gestión de Carteras</Typography>
                </Box>

                <FormControl size="small" sx={{ minWidth: 180 }}>
                    <InputLabel>Línea de negocio</InputLabel>
                    <Select value={selectedBL} label="Línea de negocio"
                        onChange={e => setSelectedBL(e.target.value)}>
                        {blOptions.map(bl => (
                            <MenuItem key={bl.id} value={bl.id}>{bl.name}</MenuItem>
                        ))}
                    </Select>
                </FormControl>

                <FormControl size="small" sx={{ minWidth: 220 }} disabled={!selectedBL || agents.length === 0}>
                    <InputLabel>Consultor</InputLabel>
                    <Select value={selectedAgent} label="Consultor"
                        onChange={e => setSelectedAgent(e.target.value)}>
                        {agents.map(a => (
                            <MenuItem key={a.userId} value={a.userId}>{a.name}</MenuItem>
                        ))}
                    </Select>
                </FormControl>

                {selectedAgent && (
                    <Button
                        variant="contained"
                        startIcon={<PersonAddIcon />}
                        onClick={() => setAddOpen(true)}
                        sx={{ ml: 'auto', textTransform: 'none', bgcolor: '#007AFF', '&:hover': { bgcolor: '#005ecb' } }}
                    >
                        Agregar cliente
                    </Button>
                )}
            </Box>

            {/* Contenido */}
            <Box sx={{ flex: 1, overflowY: 'auto', px: 3, py: 2 }}>
                {!selectedAgent ? (
                    <Box sx={{ textAlign: 'center', mt: 8, color: 'text.secondary' }}>
                        <GroupIcon sx={{ fontSize: 56, color: '#7c3aed', opacity: 0.3 }} />
                        <Typography variant="h6" fontWeight={600} color="text.secondary" mt={1}>
                            Selecciona un consultor
                        </Typography>
                        <Typography variant="body2">
                            Elige una línea de negocio y un consultor para ver y gestionar su cartera.
                        </Typography>
                    </Box>
                ) : loadingCartera ? (
                    [1, 2, 3].map(i => <Skeleton key={i} height={56} sx={{ mb: 1 }} />)
                ) : (
                    <Box>
                        <Typography variant="body2" color="text.secondary" mb={1.5}>
                            Cartera de <strong>{agentName}</strong> · {cartera.length} cliente{cartera.length !== 1 ? 's' : ''}
                        </Typography>

                        {cartera.length === 0 ? (
                            <Box sx={{ textAlign: 'center', mt: 6, color: 'text.secondary' }}>
                                <Typography variant="body2">Este consultor no tiene clientes en su cartera.</Typography>
                            </Box>
                        ) : (
                            <Box sx={{ border: '1px solid #e5e7eb', borderRadius: 2, overflow: 'hidden' }}>
                                {cartera.map((row, idx) => (
                                    <Box key={row.id ?? row.clientId}>
                                        {idx > 0 && <Divider />}
                                        <Box sx={{
                                            display: 'flex', alignItems: 'center', gap: 2, px: 2, py: 1.25,
                                            '&:hover': { bgcolor: '#f5f5f5' },
                                        }}>
                                            <Avatar sx={{ bgcolor: '#007AFF', width: 36, height: 36, fontSize: 13 }}>
                                                {getInitials(row.clientName)}
                                            </Avatar>
                                            <Box sx={{ flex: 1, minWidth: 0 }}>
                                                <Typography fontWeight={600} fontSize={14} noWrap>
                                                    {row.clientName || 'Cliente sin nombre'}
                                                </Typography>
                                                <Typography variant="caption" color="text.secondary">
                                                    {fmtPhone(row.clientPhone)}
                                                </Typography>
                                            </Box>
                                            <Chip
                                                label={row.assignmentType === 'manual' ? 'Manual' : 'Automática'}
                                                size="small" variant="outlined" sx={{ fontSize: 11, height: 20 }}
                                            />
                                            <Box sx={{ display: 'flex', gap: 1, flexShrink: 0 }}>
                                                <Tooltip title="Reasignar a otro consultor">
                                                    <Button size="small" variant="outlined"
                                                        startIcon={<SwapHorizIcon />}
                                                        onClick={() => { setReassignRow(row); setReassignTarget(''); }}
                                                        sx={{ textTransform: 'none', fontSize: 12 }}>
                                                        Reasignar
                                                    </Button>
                                                </Tooltip>
                                                <Tooltip title="Quitar de la cartera">
                                                    <IconButton size="small" color="error"
                                                        disabled={working}
                                                        onClick={() => handleRemove(row)}>
                                                        <DeleteOutlineIcon fontSize="small" />
                                                    </IconButton>
                                                </Tooltip>
                                            </Box>
                                        </Box>
                                    </Box>
                                ))}
                            </Box>
                        )}
                    </Box>
                )}
            </Box>

            {/* Diálogo: agregar cliente */}
            <Dialog open={addOpen} onClose={() => { setAddOpen(false); setSearch(''); setSearchResults([]); }}
                maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: 3 } }}>
                <DialogTitle sx={{ fontWeight: 700 }}>Agregar cliente a la cartera de {agentName}</DialogTitle>
                <DialogContent>
                    <TextField
                        fullWidth size="small" autoFocus sx={{ mt: 1 }}
                        label="Buscar cliente" placeholder="Nombre, teléfono o RUT..."
                        value={search} onChange={e => setSearch(e.target.value)}
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position="start">
                                    <PersonSearchIcon sx={{ fontSize: 18, color: '#999' }} />
                                </InputAdornment>
                            ),
                            endAdornment: searching && (
                                <InputAdornment position="end"><CircularProgress size={14} /></InputAdornment>
                            ),
                        }}
                    />
                    <Box sx={{ mt: 1, maxHeight: 280, overflowY: 'auto' }}>
                        {searchResults.map(c => (
                            <Box key={c.id}
                                sx={{
                                    display: 'flex', alignItems: 'center', gap: 1.5, px: 1, py: 1,
                                    borderRadius: 1.5, cursor: working ? 'default' : 'pointer',
                                    '&:hover': { bgcolor: '#f5f5f5' },
                                }}
                                onClick={() => !working && handleAddClient(c)}
                            >
                                <Avatar sx={{ bgcolor: '#007AFF', width: 32, height: 32, fontSize: 12 }}>
                                    {getInitials(c.fullName || c.name)}
                                </Avatar>
                                <Box sx={{ minWidth: 0 }}>
                                    <Typography variant="body2" fontWeight={600} noWrap>
                                        {c.fullName || c.name || fmtPhone(c.whatsAppId)}
                                    </Typography>
                                    <Typography variant="caption" color="text.secondary">
                                        {fmtPhone(c.whatsAppId)}
                                    </Typography>
                                </Box>
                            </Box>
                        ))}
                        {!searching && search.length >= 2 && searchResults.length === 0 && (
                            <Typography variant="body2" color="text.secondary" textAlign="center" py={2}>
                                Sin resultados.
                            </Typography>
                        )}
                    </Box>
                    <Typography variant="caption" color="text.secondary">
                        Si el cliente ya pertenece a otro consultor, se reasignará a {agentName}.
                    </Typography>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => { setAddOpen(false); setSearch(''); setSearchResults([]); }}
                        sx={{ textTransform: 'none' }}>Cerrar</Button>
                </DialogActions>
            </Dialog>

            {/* Diálogo: reasignar */}
            <Dialog open={!!reassignRow} onClose={() => setReassignRow(null)} maxWidth="xs" fullWidth
                PaperProps={{ sx: { borderRadius: 3 } }}>
                <DialogTitle sx={{ fontWeight: 700 }}>Reasignar cliente</DialogTitle>
                <DialogContent>
                    <Typography variant="body2" sx={{ mb: 2 }}>
                        Mover a <strong>{reassignRow?.clientName || 'este cliente'}</strong> a otro consultor de la línea.
                    </Typography>
                    <FormControl fullWidth size="small">
                        <InputLabel>Nuevo consultor</InputLabel>
                        <Select value={reassignTarget} label="Nuevo consultor"
                            onChange={e => setReassignTarget(e.target.value)}>
                            {agents.filter(a => a.userId !== selectedAgent).map(a => (
                                <MenuItem key={a.userId} value={a.userId}>{a.name}</MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setReassignRow(null)} sx={{ textTransform: 'none' }}>Cancelar</Button>
                    <Button variant="contained" disabled={!reassignTarget || working}
                        onClick={handleReassign}
                        startIcon={working ? <CircularProgress size={14} sx={{ color: '#fff' }} /> : <SwapHorizIcon />}
                        sx={{ textTransform: 'none', bgcolor: '#7c3aed', '&:hover': { bgcolor: '#6d28d9' } }}>
                        Reasignar
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Feedback */}
            <Snackbar open={Boolean(feedback)} autoHideDuration={6000} onClose={() => setFeedback(null)}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}>
                {feedback ? (
                    <Alert severity={feedback.type} onClose={() => setFeedback(null)} sx={{ width: '100%' }}>
                        {feedback.msg}
                    </Alert>
                ) : undefined}
            </Snackbar>
        </Box>
    );
};

export default GestionCarterasPage;
